import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Spinner } from '../components/ui';
import { useToast } from '../components/Toast';
import { IconCheck } from '../components/icons';
import { setMyPlan, startCheckout, verifyPayment } from '../lib/api';
import { openRazorpayCheckout } from '../lib/razorpay';
import { PRICING_TIERS as TIERS } from '../lib/plans';
import { SESSION_KEY, useSession } from '../features/auth/useSession';

/** Whole days until an ISO date, or null. */
function daysLeft(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return ms > 0 ? Math.ceil(ms / 86_400_000) : 0;
}

export function PricingPage() {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();

  const refreshSession = (u: Parameters<typeof queryClient.setQueryData>[1]) => {
    queryClient.setQueryData(SESSION_KEY, u);
    void queryClient.invalidateQueries();
  };

  // Paid plans → Razorpay checkout.
  const buy = useMutation({
    mutationFn: async (plan: 'PRO' | 'BUSINESS') => {
      const order = await startCheckout(plan);
      const opened = await openRazorpayCheckout(
        order,
        { name: user?.name, email: user?.email },
        async (r) => {
          const updated = await verifyPayment({
            orderId: r.razorpay_order_id,
            paymentId: r.razorpay_payment_id,
            signature: r.razorpay_signature,
          });
          refreshSession(updated);
          toast(`You're on the ${plan[0] + plan.slice(1).toLowerCase()} plan`);
        },
      );
      if (!opened) throw new Error('Could not open the payment gateway.');
    },
    onError: (e: Error) => toast(e.message),
  });

  // Downgrade to Free = cancel. Confirmed first — it ends the paid plan.
  const [confirmDowngrade, setConfirmDowngrade] = useState(false);
  const downgrade = useMutation({
    mutationFn: () => setMyPlan('FREE'),
    onSuccess: (u) => {
      refreshSession(u);
      setConfirmDowngrade(false);
      toast("You're on the Free plan");
    },
    onError: (e: Error) => toast(e.message),
  });

  const days = daysLeft(user?.currentPeriodEnd);

  return (
    <div className="h-full overflow-y-auto bg-neutral-50">
      <div className="mx-auto max-w-5xl px-6 py-12">
        <div className="mb-10 text-center">
          <h1 className="text-3xl font-bold tracking-tight text-neutral-900">Plans that scale with your title</h1>
          <p className="mt-2 text-neutral-500">Start free. Upgrade when your review process grows.</p>
          {user?.plan !== 'FREE' && days != null && (
            <p className="mt-2 text-sm font-medium text-neutral-600">
              Your {user!.plan[0] + user!.plan.slice(1).toLowerCase()} plan{' '}
              {days > 0 ? `expires in ${days} day${days === 1 ? '' : 's'}` : 'has expired'}.
            </p>
          )}
          <Link to="/app" className="mt-3 inline-block text-sm text-neutral-500 hover:text-neutral-900">
            ← Back to app
          </Link>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {TIERS.map((tier) => {
            const active = user?.plan === tier.plan;
            const busy = buy.isPending && buy.variables === tier.plan;
            return (
              <div
                key={tier.plan}
                className={`relative flex flex-col rounded-2xl border bg-white p-6 shadow-sm transition ${
                  active ? 'border-emerald-500 ring-2 ring-emerald-500 shadow-md' : 'border-neutral-200'
                }`}
              >
                {active && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-emerald-600 px-3 py-0.5 text-xs font-semibold text-white">
                    ✓ Your current plan
                  </span>
                )}
                <h2 className="text-lg font-semibold text-neutral-900">{tier.name}</h2>
                <p className="mt-1 text-sm text-neutral-500">{tier.tagline}</p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-3xl font-bold tracking-tight">{tier.price}</span>
                  <span className="text-sm text-neutral-400">{tier.period}</span>
                </div>
                <ul className="mt-5 flex-1 space-y-2">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-neutral-600">
                      <IconCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  {active ? (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 py-2 text-center text-sm font-medium text-emerald-700">
                      {tier.plan !== 'FREE' && days != null ? `Active · ${days}d left` : 'Current plan'}
                    </div>
                  ) : tier.plan === 'FREE' ? (
                    confirmDowngrade ? (
                      <div className="flex flex-col gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-center">
                        <span className="text-xs text-red-700">
                          Cancel your paid plan and downgrade to Free?
                        </span>
                        <div className="flex gap-2">
                          <Button
                            variant="danger"
                            onClick={() => downgrade.mutate()}
                            disabled={downgrade.isPending}
                            className="flex-1 px-2 py-1.5 text-xs"
                          >
                            {downgrade.isPending ? <Spinner /> : 'Confirm'}
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => setConfirmDowngrade(false)}
                            className="flex-1 border border-neutral-200 px-2 py-1.5 text-xs"
                          >
                            Keep plan
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        onClick={() => setConfirmDowngrade(true)}
                        className="w-full border border-neutral-200 text-sm"
                      >
                        Downgrade to Free
                      </Button>
                    )
                  ) : (
                    <Button
                      onClick={() => buy.mutate(tier.plan as 'PRO' | 'BUSINESS')}
                      disabled={buy.isPending}
                      className="w-full text-sm"
                    >
                      {busy ? <Spinner /> : `Choose ${tier.name}`}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-8 text-center text-xs text-neutral-400">
          Paid plans are billed monthly per account via Razorpay. Your plan applies to your account and all
          workspaces you own; it renews when you pay for another month.
        </p>
      </div>
    </div>
  );
}
